import React, { useCallback, useEffect, useState } from 'react'

import styled from '@emotion/styled'
import {
  AlignItems,
  Box,
  Button,
  ButtonColorVariant,
  Col,
  Display,
  FlexDirection,
  Grid,
  JustifyContent,
} from '@gousto-internal/citrus-react'
import { CopyConfirmation } from 'SocialLinks/CopyConfirmation'
import { appendNativeShareUtm, SHARE_SURFACES, shareNatively } from 'SocialLinks/shareNatively'
import { getMessage, SHARE_TITLE, SOCIAL_TYPES } from 'SocialLinks/socialReferralHelper'
import copy from 'copy-to-clipboard'

import { actionTypes } from 'actions/actionTypes'

import { RAFLinkShare } from '../RAFLinkShare'
import type { IRAFOffer } from '../ReferralPresentation'
import { getTimeDifference } from '../timeHelper'
import { RAFBoostCountdown } from './RAFBoostCountdown'
import { RAFBoostHeader } from './RAFBoostHeader'
import { RAFBoostInstructions } from './RAFBoostInstructions'
import { RAFBoostOfferBoxes } from './RAFBoostOfferBoxes'

const AlignedContainer = styled(Box)`
  @media (min-width: 885px) {
    padding-bottom: 40px;
  }
  @media (max-width: 886px) {
    margin: 0 16px;
    padding-bottom: 24px;
  }
`

const StickyMobileShareButton = styled(Box)`
  padding: 16px;
  background: white;
  position: sticky;
  bottom: 0;
  width: 100%;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (min-width: 886px) {
    display: none;
  }
`

const RelativeBox = styled(Box)`
  position: relative;
  margin-bottom: -25px;
`

const FullWidthButton = styled(Button)`
  padding: 8px 16px;
  width: 100%;
`

interface Props {
  rafOffer: IRAFOffer
  expiry: Date
  userRAFLink: string
  userFirstName: string
  device: string
  trackingReferFriendSocialSharing: () => void
  trackUserFreeFoodLinkShare: (payload: { target: string }) => void
  trackingReferFriend: (actionType: string, actionName: string) => void
}

export const BoostedRafSharing = ({
  rafOffer,
  userFirstName,
  device,
  userRAFLink,
  expiry,
  trackUserFreeFoodLinkShare,
  trackingReferFriendSocialSharing,
  trackingReferFriend,
}: Props) => {
  const credit = rafOffer.get('creditFormatted') as string
  const { days, hours, minutes, seconds } = getTimeDifference(expiry)
  const [expiryDays, setExpiryDays] = useState<number>(days)
  const [expiryHours, setExpiryHours] = useState<number>(hours)
  const [expiryMinutes, setExpiryMinutes] = useState<number>(minutes)
  const [expirySeconds, setExpirySeconds] = useState<number>(seconds)
  const [isCopied, setIsCopied] = useState<boolean>(false)

  const onFallback = useCallback(() => {
    if (!copy(userRAFLink)) {
      return
    }

    setIsCopied(true)
    trackingReferFriend(actionTypes.REFER_FRIEND_LINK_COPIED, 'ReferFriendLink Copied')
    trackUserFreeFoodLinkShare({ target: SOCIAL_TYPES.link })
  }, [userRAFLink, trackingReferFriend, trackUserFreeFoodLinkShare])

  const onMobileShare = useCallback(() => {
    shareNatively({
      payload: {
        title: SHARE_TITLE,
        url: appendNativeShareUtm(userRAFLink),
        text: getMessage(rafOffer),
      },
      surface: SHARE_SURFACES.boostedRafPage,
      onFallback,
    })
  }, [userRAFLink, rafOffer, onFallback])

  const setTimes = useCallback(() => {
    const timeDifference = getTimeDifference(expiry)
    setExpiryDays(timeDifference.days)
    setExpiryHours(timeDifference.hours)
    setExpiryMinutes(timeDifference.minutes)
    setExpirySeconds(timeDifference.seconds)
  }, [expiry])

  useEffect(() => {
    setInterval(() => {
      setTimes()
    }, 1000)
  }, [setTimes])

  return (
    <RelativeBox>
      <Grid alignItems={AlignItems.Center} justifyContent={JustifyContent.Center}>
        <Col
          size={[12, 8, 8, 8]}
          gridColumn={['1 / span 12', '3 / span 8', '3 / span 8', '3 / span 8']}
          display={Display.Flex}
          justifyContent={JustifyContent.Center}
        >
          <AlignedContainer
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            justifyContent={JustifyContent.Center}
            alignItems={AlignItems.Center}
          >
            <Box paddingBottom={6}>
              <RAFBoostHeader />
              <RAFBoostOfferBoxes rafOffer={rafOffer} />
            </Box>
            <RAFBoostCountdown
              days={expiryDays}
              minutes={expiryMinutes}
              hours={expiryHours}
              seconds={expirySeconds}
            />
            <RAFLinkShare
              userRAFLink={userRAFLink}
              userFirstName={userFirstName}
              trackingReferFriendSocialSharing={trackingReferFriendSocialSharing}
              device={device}
              trackUserFreeFoodLinkShare={trackUserFreeFoodLinkShare}
              trackingReferFriend={trackingReferFriend}
              isBoost
              credit={credit}
            />
          </AlignedContainer>
        </Col>
      </Grid>
      <Grid>
        <Col size={12}>
          <RAFBoostInstructions rafOffer={rafOffer} />
        </Col>
      </Grid>
      <StickyMobileShareButton>
        <FullWidthButton
          colorVariant={ButtonColorVariant.Primary}
          onClick={onMobileShare}
          width="100%"
        >
          Share your link
        </FullWidthButton>
        {isCopied && <CopyConfirmation credit={credit} />}
      </StickyMobileShareButton>
    </RelativeBox>
  )
}
